/**
 * THE ONE PLACE A PRICE BECOMES TEXT.
 *
 * `docs/PRICING.md` asks for a single source for what a membership costs, and the catalogue table
 * is that. This is the other half of the same requirement: one FORMATTER, so the public page, the
 * checkout screen, the commissioner's list and an email cannot render the same amount three ways.
 *
 * Pure, and in `shared` rather than in a component, because an email template and a PDF need it
 * too and neither can import React.
 *
 * Amounts are integer cents everywhere on this platform — `plans.amount_cents`,
 * `leagues.seat_price_cents`. Floating point is not a currency: 29.99 is not representable, and
 * three of them added up is 89.96999999999998.
 */

/**
 * `$29.99`, and `$299` rather than `$299.00`.
 *
 * The trailing `.00` is dropped deliberately. A price list reading "$29.99 / $299" is scanned in
 * one glance; "$29.99 / $299.00" makes the eye check the second one. Cents are shown whenever
 * there are any, so nothing is ever rounded away.
 */
export function formatMoney(amountCents: number, currency = 'usd', fixedDecimals = false): string {
  const whole = !fixedDecimals && amountCents % 100 === 0;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amountCents / 100);
}

/** `month` → `/month`. What a subscription price is followed by, and nothing for a one-off. */
export function intervalSuffix(interval: 'month' | 'year' | 'one_time'): string {
  if (interval === 'month') return '/month';
  if (interval === 'year') return '/year';
  return '';
}

/** `$29.99/month`. The whole thing, for the places that show a price on one line. */
export function formatPrice(plan: {
  amountCents: number;
  currency?: string;
  interval: 'month' | 'year' | 'one_time';
}): string {
  return `${formatMoney(plan.amountCents, plan.currency ?? 'usd')}${intervalSuffix(plan.interval)}`;
}

/**
 * WHAT PAYING YEARLY SAVES, as an amount, ROUNDED DOWN to the whole dollar.
 *
 * Down rather than nearest, always. At $29.99 a month the real saving is $60.88 and the honest
 * headline is "Save $60" — rounding up to $61 would put a number on a page that is larger than
 * what anybody actually saves, which is the one direction a price claim must never be wrong in.
 *
 * `docs/PRICING.md` describes annual as "twelve months for the price of ten". That is the intent
 * and it is very nearly true — $299 is 9.97 months at $29.99 — but it is not arithmetic, so it is
 * not computed here. Derived copy has to survive the commissioner changing a price, and a sentence
 * asserting a clean ratio would start lying the moment one moved.
 *
 * Null when yearly costs the same or more, so the badge simply does not appear rather than
 * claiming a saving of nothing.
 */
export function annualSavingCents(monthlyCents: number, annualCents: number): number | null {
  if (monthlyCents <= 0 || annualCents <= 0) return null;

  const saved = monthlyCents * 12 - annualCents;
  if (saved <= 0) return null;

  return Math.floor(saved / 100) * 100;
}

/** What a yearly plan works out at per month, for the line under the price. */
export function perMonthCents(annualCents: number): number {
  return Math.round(annualCents / 12);
}
